/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */

import Raphael from 'raphael';

export class PlotViewElements {

    /**
     * Create the plot background rect SVG element.
     * @param Dyn - used in closures for click handlers
     * @param plotView - used in closures for click handlers
     * @returns {*} - paper.rect
     */
    static makeBackgroundRect( Dyn, plotView ) {
        let backgroundRect = plotView.paper
            .rect( 0, 0, 0, 0 )
            .attr( { fill: Dyn.plotUtil.transparentContainer, stroke: "none" } )
            .hover(
                backgroundHoverStart, // hover ignored by touch devices
                backgroundHoverEnd );
        // Enable drag handlers for marquee selection if:
        // 1. Not a touch device, OR
        // 2. Touch device with a fine pointer (e.g., laptop with trackpad and touch screen)
        const enableDragForMarquee = !Dyn.environment.isTouchDevice() || Dyn.environment.hasFinePointer();

        if( Dyn.environment.isTouchDevice() && !enableDragForMarquee ) {
            // Pure touch device (no fine pointer) - only click handler, no marqueeSelect
            backgroundRect.click(
                backgroundClickHandler );
        } else {
            // Desktop OR touch device with fine pointer - enable drag for marquee selection
            backgroundRect.drag(
                backgroundDragMove,
                backgroundDragStart, // also handles mouse clicks
                backgroundDragEnd );
        }
        Dyn.plotUtil.setKeyboardFocusable( backgroundRect );
        backgroundRect.node.setAttribute( "role", "main" );  // #2830 moved from div className="tde-plotview-wrapper"
        backgroundRect.node.setAttribute( "aria-describedby", "tde-plot-summary" );
        backgroundRect.node.setAttribute( "aria-label", "Plot Area" );
        return backgroundRect;

        /**
         * Handle mouse up on plot background.
         * @param event
         */
        function backgroundClickHandler( event ) {
            Dyn.mediator.publish( "records:casesSelected",      // deselect cases
                { ids: [], object: "plot", disableToggle: true, addToSelection: false }, );
            plotView.plotShaper.clearSelectedAdornments();  // deselect adornments
            plotView.setBackgroundFocus();
            // Note that click handler is currently only used for pure touch devices (no fine pointer),
            // devices with fine pointers have drag handlers, so they let backgroundDragStart() handle deselection
            Dyn.assert( Dyn.environment.isTouchDevice() && !Dyn.environment.hasFinePointer() );
        }

        function backgroundHoverStart( event ) {
            if( !plotView.isDraggingAttribute() ) {
                if( plotView.isInDragMapMode() ) {
                    event.currentTarget.style.cursor = Dyn.plotUtil.cursorHand;
                } else {
                    event.currentTarget.style.cursor = Dyn.plotUtil.cursorCrossHair;
                }
            }
        }

        function backgroundHoverEnd( event ) {
            event.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
        }

        /**
         * Handle plot background drags.
         * Also handles background clicks and touches since mouse+touch always trigger dragstart events.
         * @param x
         * @param y
         * @param event
         */
        function backgroundDragStart( x, y, event ) {
            // mouse down on background deselects all cases
            let wantAdd = Boolean( event && (event.shiftKey || event.ctrlKey || event.metaKey) ),
                caseData = { ids: [], object: "plot", disableToggle: true, addToSelection: wantAdd };
            if( !plotView.isInDragMapMode() ) {
                if( Dyn.environment.isTouchDevice() ) {
                    // to fix iPad Issue: Case Tooltip Doesn't Disappear After Unselecting #349
                    Dyn.toolTip.hide();
                    plotView.hideHoverGlow();
                }
                // clear selection
                Dyn.mediator.publish( "records:casesSelected", caseData ); // deselect cases
                plotView.plotShaper.clearSelectedAdornments(); // deselect any adornments
                plotView.setBackgroundFocus();
                // but do not start marquee select until we have dragged 1+ pixels from start
            }
        }

        function backgroundDragMove( dx, dy, x, y, event ) {
            // show and move marquee, select intersecting cases, once dragging starts
            // 		NOTE1: Use plotView.backgroundRect as stable object to convert x, y from page to plotView.paper coordinates.
            // 		NOTE2: don't rely on event.layerX, etc, since event layer changes in FireFox depending on what is under mouse.
            // 		NOTE3: datasets page seems to need the extra offset "event.pageY - event.clientY" which is 0 in other views.
            let start = Dyn.plotUtil.dragEventXYtoPaperXY( x, y, event, plotView.paper ),
                left = Math.min( start.x, start.x - dx ),
                top = Math.min( start.y, start.y - dy ),
                width = Math.abs( dx ),
                height = Math.abs( dy ),
                wantAdd = Boolean( event && (event.shiftKey || event.ctrlKey || event.metaKey) );
            if( !plotView.isInDragMapMode() ) {
                // Note: this might stop one mode (text edit), before starting marquee select
                plotView.marqueeSelect( left, top, width, height, wantAdd );
            }
        }

        function backgroundDragEnd( /*event*/ ) {
            // hide marquee
            if( plotView.isInMarqueeSelect() ) {
                plotView.stopViewMode();
                plotView.setBackgroundFocus();
            }
        }
    } // setBackgroundHandlers

    /**
     * Create the plot excluded cases rect SVG element.
     * This is a nearly invisible rectangle over the plot area that handles hover and click events
     * @param Dyn - used in closures for click handlers
     * @param plotView - used in closures for click handlers
     * @returns {*} - paper.rect
     */
    static makeExcludedCasesRect( Dyn, paper, dataSet ) {
        let rectElement = paper.rect( 0, 0, 0, 0 )
            .attr( Dyn.debugViewRects ?
                { fill: "none", stroke: "orange", opacity: 0.5 } : // make visible for debugging
                { fill: "gray", stroke: "none", opacity: Dyn.plotUtil.opacityInvisibleButDraggable } )
            .data( "tooltip", "" )
            .data( "numMissing", 0 )
            .data( "numFiltered", 0 )
            .hover(
                excludedCasesHoverOver,
                excludedCasesHoverOut )
            .click( excludedCasesClick )
            .hide();
        Dyn.plotUtil.setKeyboardFocusable( rectElement );
        rectElement.node.setAttribute( 'role', 'img' );
        rectElement.node.onfocus = excludedCasesFocusHandler; // on focus, set aria-label, show tooltip, etc
        rectElement.node.onblur = excludedCasesBlurHandler;
        // NOTE: text for tooltip and 'aria-label' set dynamically by ExcludedCasesDisplay.updateVisuals()
        return rectElement;

        // keyboard focus
        function excludedCasesFocusHandler( event ) {
            rectElement.node.setAttribute( 'aria-label', rectElement.data( 'tooltip' ));
            // show tool tip on keyboard focus
            showToolTip( rectElement, event );
        }

        // when keyboard focus leaves this icon
        function excludedCasesBlurHandler( event ) {
            rectElement.node.setAttribute( 'aria-label', '' );
            if( Dyn.toolTip.isMyElement( rectElement ) )
                Dyn.toolTip.hide();
        }

        function showToolTip( rectElement, event ) {
            Dyn.assert( rectElement.data( 'tooltip' ));
            Dyn.toolTip.show( rectElement, event, {
                x: rectElement.getBBox().x, y: rectElement.getBBox().y2, alignRight: rectElement.data( "alignRight" )
            } );
        }

        function excludedCasesHoverOver( event ) {
            showToolTip( this, event );
        }

        function excludedCasesHoverOut() {
            if( Dyn.toolTip.isMyElement( this ) )
                Dyn.toolTip.hide();
        }

        function excludedCasesClick( /*event*/ ) {
            // select the excluded cases, so they are visible in table and 1st visible in cards
            let casesSelectedData = { ids: [] };
            for( var i = 0, j = dataSet.getNumCasesPreFilter(); i < j; i++ ) {
                if( !dataSet.isCaseNonMissingNonFiltered( i ) ) {
                    casesSelectedData.ids.push( i );
                }
            }
            if( casesSelectedData.ids.length > 0 ) {
                Dyn.mediator.publish( "records:casesSelected", casesSelectedData );
            }
            this.node.focus();
        }
    } // makeExcludedCasesRect

} // class PlotViewElements

export class PlotViewIcons {
    /**
     * Add handler functions for the case icon/bar section/pie slice element.
     * Note: bar sections and pie slices are actually formed from the first case icon in the section
     * @param Dyn - used in closures for click handlers
     * @param plotView - used in closures for click handlers
     */
    static makeCaseIconHandlers( Dyn, plotView, newIcon ) {
        // Enable drag handlers if not a touch device, or if touch device has a fine pointer (trackpad)
        const enableDragHandlers = !Dyn.environment.isTouchDevice() || Dyn.environment.hasFinePointer();

        if(Dyn.environment.isTouchDevice() && !enableDragHandlers) {
            // Pure touch device (no fine pointer) - no touchstart(), touchend(), or hover()
            newIcon.node.onclick = iconTouchClickHandler;
        } else {
            // Desktop OR touch device with fine pointer - enable hover and drag
            newIcon.hover( iconHoverStart, iconHoverEnd ); // mouse events not touch events
            newIcon.node.onkeydown = iconKeyDownHandler;
            newIcon.drag(
                iconDragMove,
                iconDragStart, // also handles click and touchstart events since dragstart always called then also.
                iconDragEnd );
        }
        newIcon.node.onfocus = iconFocusHandler; // on focus, set aria-label, show tooltip, etc
        newIcon.node.onblur = iconBlurHandler;
        return;

        // Keyboard control for case icon circles/bar sections/pie slices
        function iconKeyDownHandler( e ) {
            const wantToggleSelection = true;
            switch( e.key ) { // const {key} = event; ES6+
                case "Enter":
                    PlotViewIcons.selectCase( Dyn, plotView, newIcon, e, wantToggleSelection);
                    newIcon.node.focus();
                    break;
                case "Escape":
                    PlotViewIcons.hideToolTip( Dyn, newIcon );
                    break;
            }
        }

        // keyboard focus
        function iconFocusHandler( event ) {
            // aria-label population for the cases in plot area.
            newIcon.node.setAttribute( "aria-label", PlotViewIcons.getAriaLabel( Dyn, plotView, newIcon, event) );
            // show tool tip on keyboard focus
            PlotViewIcons.showToolTip( Dyn, plotView, newIcon, event );
        }

        // when keyboard focus leaves this icon
        function iconBlurHandler( event ) {
            newIcon.node.setAttribute( "aria-label", '' );
            PlotViewIcons.hideToolTip( Dyn, newIcon );
        }

        /**
         * Handle case icon drags.
         * Also handles icon clicks and touches since mouse+touch always trigger dragstart events.
         * @param x
         * @param y
         * @param event
         */
         function iconDragStart( x, y, event ) {
            let iconElement = this;

            // store original coordinates for dragMove()
            iconElement.data( "ox", this.attr("cx"));
            iconElement.data( "oy", this.attr("cy"));

            PlotViewIcons.selectCase( Dyn, plotView, this, event);
            Dyn.plotUtil.setAsKeyboardFocus( this );

            let caseIndex = iconElement.data("caseIndex"),
                iconIndex = iconElement.data("iconIndex" );
            Dyn.sound.playOneCase( plotView, caseIndex, iconIndex );
        }

        function iconDragMove( dx, dy, x, y, event ) {
            let iconElement = this,
                isMixup = plotView.plotShaper.hasElement( Dyn.EPlotControl.kControlsMixup );

            if( isMixup && ! plotView.isInMarqueeSelect()) {
                // update element position (assume circle if we have a mixup plot)
                let newX = iconElement.data("ox") + dx,
                    newY = iconElement.data("oy") + dy;

                // move case icon, glow, and tooltip
                plotView.dragCaseUpdate( iconElement.data("iconIndex"), newX, newY );
                plotView.showHoverGlow( iconElement );
                PlotViewIcons.showToolTip( Dyn, plotView, iconElement, event );
            }
        }

        function iconDragEnd( event ) {
            // remove temporary data
            this.removeData( "ox" );
            this.removeData( "oy" );

            // show tooltip and glow again
            let iconElement = this,
                bbox = this.getBBox(),
                xpos = event.offsetX,
                ypos = event.offsetY,
                cursorOverElement = xpos && Raphael.isPointInsideBBox(bbox, xpos, ypos );

            if( cursorOverElement && ! plotView.isInMarqueeSelect()) {
                plotView.showHoverGlow( this );
                PlotViewIcons.showToolTip( Dyn, plotView, iconElement, event );
            } else {
                plotView.hideHoverGlow();
                PlotViewIcons.hideToolTip( Dyn, iconElement );
            }
            Dyn.plotUtil.setAsKeyboardFocus( this );
        }

	    function iconHoverStart( event ) {
            if( ! plotView.isDraggingAttribute()) {
                if( !plotView.isInMarqueeSelect() ) {

                    event.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault; // undo backgroundHoverStart()'s cursor change

                    // change border color
                    this.data("borderColor", this.attr("stroke"));
                    this.attr( {stroke: Dyn.plotUtil.kCaseHoverBorderColor} );

                    // show glow underneath
                    plotView.showHoverGlow( this );
                }
                // show tool tip text on top
                PlotViewIcons.showToolTip( Dyn, plotView, this, event );
            }
        }

        function iconHoverEnd() {
            let iconElement = this,
                borderColor = iconElement.data("borderColor");

            PlotViewIcons.hideToolTip( Dyn, iconElement );

            iconElement.animate(
                { stroke: borderColor },
                Dyn.plotUtil.quickAnimationTimeMs, Dyn.plotUtil.quickAnimationEasing );

            plotView.hideHoverGlow();
        }

        function iconTouchClickHandler( event ) {
            PlotViewIcons.selectCase( Dyn, plotView, newIcon, event);
        }
    } // makeCaseIconHandlers

    /**
     * get 'tootip' text so that it can be used for aria lable, as well as
     * to display tooltip on hover in.
     * @param {*} iconElement
     * @param {*} event
     */
    static getToolTip( Dyn, plotView, iconElement, event ) {
        let txt = "",
            xAxis = plotView.xAxis,
            yAxis = plotView.yAxis,
            hasMultiCaseSections = plotView.plotShaper.hasElement( Dyn.EPlotControl.kControlsMultiCaseSections ),
            hasNumericIntervals = plotView.plotShaper.hasElement( Dyn.EPlotControl.kControlsNumericIntervals ),
            isCaseOfSample = plotView.dataSet.caseSampler.isSampleSelected(),
            isCaseOfSampleDistribution = plotView.dataSet.caseSampler.isDistributionSelected(),
            caseIndex = iconElement.data("caseIndex"),
            iconIndex = iconElement.data("iconIndex" ),
            iconPosition = plotView.casePositions[iconIndex],
            attrSet = new Set(), // set of attributes already displayed, to avoid redundant lines if attr used twice.
            attr;

        function caseValueStringOrMissing( caseIndex, attr ) {
            var caseValueString = attr.caseValueToString( caseIndex );
            if( ! caseValueString ) {
                caseValueString = Dyn.loc( Dyn.dataUtil.kMissingValueName );
            }
            return Dyn.loc( "{{attribute}}: {{value}}", { attribute:attr.name, value:caseValueString });
        }
        function numericIntervalStringOrMissing( caseIndex, attr ) {
            var sectionText = attr.getNumericIntervalString( caseIndex );
            if( ! sectionText ) {
                sectionText = Dyn.loc( Dyn.dataUtil.kMissingValueName );
            }
            return Dyn.loc( "{{attribute}}: {{value}}", { attribute:attr.name, value:sectionText });
        }
        function getExtraToolTipForSection( iconIndex ) {
            var sectionText = '',
                position    = plotView.casePositions[iconIndex],// NOTE: must use iconIndex not caseIndex, for multiple attributes on axis
                plotElement = plotView.plotShaper.findElement( Dyn.EPlotControl.kControlsShape );

            if( plotElement && plotElement.getExtraTooltipForCase ) {
                sectionText = plotElement.getExtraTooltipForCase( caseIndex, position.cellIndex, position.sectionIndex );
            }
            return sectionText;
        }
        function showAxisCaseAttributeValue( axisView, attrIndex, hasMultiCaseSections ) {
            return attrIndex >= 0 &&
                attrIndex < axisView.allAttributes.countActiveIncludingDualAxis() && // is an active attribute (hide inactive)
                ((! hasMultiCaseSections) || axisView.isCategoricalAxis()); // if section plot only show if categorical value
        }

        // add selected attribute value if any
        attr = plotView.rightMargin.getColorAttribute();
        if( attr && ! attrSet.has(attr) ) {
            attrSet.add( attr );
            if( hasNumericIntervals ) {
                txt += (txt?"\n":"") + numericIntervalStringOrMissing( caseIndex, attr );
            } else {
                txt += (txt ? "\n" : "") + caseValueStringOrMissing( caseIndex, attr );
            }
        }

        // add order-by attribute value if any and not the same as selected attribute
        attr = plotView.plotShaper.dataSet.getOrderByAttribute();
        if( plotView.plotShaper.hasCustomOrderBy() && ! attrSet.has(attr)) {
            attrSet.add( attr );
            txt += (txt ? "\n" : "") + caseValueStringOrMissing( caseIndex, attr );
        }

        // add X-axis value if any (use correct attribute if multiple attributes on axis)
        if( showAxisCaseAttributeValue( xAxis, iconPosition.xAttrIndex, hasMultiCaseSections )){
            attr = xAxis.allAttributes.attributes[iconPosition.xAttrIndex];
            if( ! attrSet.has(attr) ){
                attrSet.add( attr );
                txt += (txt?"\n":"") + caseValueStringOrMissing( caseIndex, attr );
            }
        }

        // Add yaxis value if any (use correct attribute if multiple attributes on axis)
        if( showAxisCaseAttributeValue( yAxis, iconPosition.yAttrIndex, hasMultiCaseSections )) {
            attr = yAxis.getActiveAttribute( iconPosition.yAttrIndex );
            if( ! attrSet.has( attr ) ){
                attrSet.add( attr );
                txt += (txt?"\n":"") + caseValueStringOrMissing( caseIndex, attr );
            }
        }

        // add section or case tip
        if( hasMultiCaseSections ) {
            txt += (txt?"\n":"") + getExtraToolTipForSection( iconElement.data("iconIndex"));
        } else if( isCaseOfSampleDistribution ) {
            txt += (txt?"\n":"") + Dyn.loc( "Sample No. {{n}}", {n: (caseIndex + 1)} );
        } else {
            // NOTE: possibly use DataSet.getTooltipAttributes() here, like Tuva Jr.
            if( isCaseOfSample ) { // get case number of population
                attr = plotView.dataSet.attribs[0]; // stored first attribute ("Case")
                caseIndex = attr && attr.cases[ caseIndex ];
            } else {
                caseIndex += 1;
            }
            txt += (txt?"\n":"") + Dyn.loc( "Case: {{n}}", {n: caseIndex } );
        }

        return txt;
    }

    /**
     * Select the case or category associated with the given icon in the plot
     * @param iconElement - case icon/bar section/pie slice
     * @param event
     */
    static selectCase( Dyn, plotView, iconElement, event, wantToggle ) {
        var selectedAttribute = plotView.dataSet.getSelectedAttribute(),
            wantAdd = Boolean( event && (event.shiftKey || event.ctrlKey || event.metaKey) ),
            caseIndex = iconElement.data("caseIndex" ),
            selectData = { object: "plot", disableToggle: false,
                addToSelection: wantAdd, toggleSelect:wantToggle },
            isMultiCaseSectionsPlot = plotView.plotShaper.hasElement( Dyn.EPlotControl.kControlsMultiCaseSections ),
            isNumericIntervalsPlot = plotView.plotShaper.hasElement( Dyn.EPlotControl.kControlsNumericIntervals );

        if( ! isMultiCaseSectionsPlot ) {
            // select one case
            iconElement.toFront();
            selectData.ids = [ caseIndex ];
            Dyn.mediator.publish( "records:casesSelected", selectData );
        } else {
            if ( selectedAttribute ) {
                // select the cases with the given category for this attribute
                selectData.attributeID = selectedAttribute.id;
                selectData.category = selectedAttribute.cases[ caseIndex ];
                if( isNumericIntervalsPlot ) {
                    selectData.category = selectedAttribute.getNumericIntervalString( caseIndex );
                    selectData.isNumericInterval = Boolean( selectData.category );
                }
                Dyn.mediator.publish( "records:casesSelectedByCategory", selectData );
            }
            // else do nothing if no selected attribute?
        }
    }

    /** Get the aria label for the given icon in the plot */
    static getAriaLabel( Dyn, plotView, iconElement, event ) {
        var txt = "",
            plot_components = [],
			xAxis = plotView.xAxis,
			yAxis = plotView.yAxis,
            hasMultiCaseSections = plotView.plotShaper.hasElement( Dyn.EPlotControl.kControlsMultiCaseSections ),
            hasNumericIntervals = plotView.plotShaper.hasElement( Dyn.EPlotControl.kControlsNumericIntervals ),
			caseIndex = iconElement.data("caseIndex"),
            iconIndex = iconElement.data("iconIndex" ),
            iconPosition = plotView.casePositions[iconIndex],
            attrSet = new Set(), // set of attributes already displayed, to avoid redundant lines if attr used twice.
            attr;

		function caseValueStringOrMissing( caseIndex, attr ) {
			var caseValueString = attr.caseValueToString( caseIndex );
			if( ! caseValueString ) {
				caseValueString = Dyn.loc( Dyn.dataUtil.kMissingValueName );
			}
			return Dyn.loc( "{{attribute}} of {{value}}", { attribute:attr.name, value:caseValueString });
		}
        function numericIntervalStringOrMissing( caseIndex, attr ) {
            var sectionText = attr.getNumericIntervalString( caseIndex );
            if( ! sectionText ) {
                sectionText = Dyn.loc( Dyn.dataUtil.kMissingValueName );
            }
            return Dyn.loc( "{{attribute}} of {{value}}", { attribute:attr.name, value:sectionText });
        }
        function getExtraToolTipForSection( iconIndex ) {
            var sectionText = '',
                position    = plotView.casePositions[iconIndex],// NOTE: must use iconIndex not caseIndex, for multiple attributes on axis
                plotElement = plotView.plotShaper.findElement( Dyn.EPlotControl.kControlsShape );

            if( plotElement && plotElement.getExtraTooltipForCase ) {
                sectionText = plotElement.getExtraTooltipForCase( caseIndex, position.cellIndex, position.sectionIndex );
            }
            return sectionText;
        }
        function showAxisCaseAttributeValue( axisView, attrIndex, hasMultiCaseSections ) {
            return attrIndex >= 0 &&
                attrIndex < axisView.allAttributes.countActive() && // is an active attribute (hide inactive)
                ((! hasMultiCaseSections) || axisView.isCategoricalAxis()); // if section plot only show if categorical value
        }

        // add section or case tip
        if( hasMultiCaseSections ) {
            txt += (txt?"\n":"") + getExtraToolTipForSection( iconElement.data("iconIndex"));
        } else {
            txt += (txt?"\n":"") + Dyn.loc( "Case {{n}}", {n: (caseIndex + 1)} );
        }

        // add X-axis value if any (use correct attribute if multiple attributes on axis)
        if( showAxisCaseAttributeValue( xAxis, iconPosition.xAttrIndex, hasMultiCaseSections )){
            attr = xAxis.allAttributes.attributes[iconPosition.xAttrIndex];
            if( ! attrSet.has(attr) ){
                attrSet.add( attr );
                plot_components.push( caseValueStringOrMissing( caseIndex, attr ) );
            }
        }

        // Add yaxis value if any (use correct attribute if multiple attributes on axis)
        if( showAxisCaseAttributeValue( yAxis, iconPosition.yAttrIndex, hasMultiCaseSections )) {
            attr = yAxis.allAttributes.attributes[iconPosition.yAttrIndex];
            if( ! attrSet.has(attr) ){
                attrSet.add( attr );
                plot_components.push( caseValueStringOrMissing( caseIndex, attr ) );
            }
        }

        // add selected attribute value if any
        attr = plotView.rightMargin.getColorAttribute();
        if( attr && ! attrSet.has(attr) ) {
            attrSet.add( attr );
            if( hasNumericIntervals ) {
                plot_components.push( numericIntervalStringOrMissing( caseIndex, attr ) );
            } else {
                plot_components.push( caseValueStringOrMissing( caseIndex, attr ) );
            }
        }

        // add case selection status
        let icon = plotView.icons[ iconIndex ],
            hasSomething = plot_components.length > 0;
        plot_components.push( Dyn.loc( icon?.data( "isSelected" ) ? "is selected" : "is not selected" ));

        if ( plot_components.length > 0 ){
            txt = txt + (hasSomething?' has ':' ') + plot_components.join(', ').replace(/, ([^,]*)$/, ' and $1');
        }

        return txt;
    }

    /**
     * show 'tooltip' text for the case/category icon we are hovering over in the plot.
     * Shows information for every attribute used in the plot.
     * @param iconElement
     * @param event
     */
	static showToolTip( Dyn, plotView, iconElement, event ) {
		// show tool tip text on top
        //  NOTE: use the mouse position where possible (best for large bars and pie slices),
        //  else use the icon center position (e.g. for keyboard or touch events)
		iconElement.data( { "tooltip": this.getToolTip( Dyn, plotView, iconElement, event ) });
		if( Dyn.toolTip.isMyElement( iconElement ))
			Dyn.toolTip.update( iconElement, event, {} );
		else
			Dyn.toolTip.show(   iconElement, event, {} );
	}

    static hideToolTip( Dyn, iconElement ) {
        iconElement.data( { "tooltip": "" });
        if( Dyn.toolTip.isMyElement( iconElement ))
            Dyn.toolTip.hide();
    }

} // class PlotViewIcons

